import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Calendar, ArrowRight, Clock, Ban,
} from 'lucide-react';

/**
 * Membership status hero widget for the Hub Interno.
 *
 * Renders one of five visual states derived from the `membership` payload of
 * `GET /api/me/dashboard`:
 *   current (al corriente) · current+expiring (por vencer) · expired (vencida)
 *   · unset (sin fecha) · suspended.
 *
 * Returns null when the user has no interpreter profile (admins without a
 * member record) so the Hub stays clean.
 */
const TEXTURE_URL =
  'https://images.unsplash.com/photo-1597773150796-e5c14ebecbf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxzdWJ0bGUlMjBhYnN0cmFjdCUyMGJsdWUlMjB0ZXh0dXJlfGVufDB8fHx8MTc4MTU2MzgyMXww&ixlib=rb-4.1.0&q=85';

function fmtDate(iso, isEs) {
  if (!iso) return '';
  try {
    return new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString(
      isEs ? 'es-MX' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' },
    );
  } catch {
    return String(iso).slice(0, 10);
  }
}

function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function MembershipStatusCard({ membership, profileHref, isEs = true }) {
  if (!membership || membership.status === 'no_profile' || !membership.status) return null;

  const days = membership.days_left;
  const expiringSoon = membership.status === 'current' && typeof days === 'number' && days <= 45;
  const owed = Number(membership.amount_owed || 0);

  const T = (es, en) => (isEs ? es : en);

  // ── AL CORRIENTE (current, not expiring) ──────────────────────────────────
  if (membership.status === 'current' && !expiringSoon) {
    const benefits = [
      T('Perfil visible en el directorio', 'Profile visible in the directory'),
      T('Acceso al Hub Interno', 'Internal Hub access'),
      T('Recepción de solicitudes de clientes', 'Client requests routing'),
      T('Foro y eventos privados', 'Private forum & events'),
    ];
    return (
      <div
        data-testid="membresia-card-activa"
        className="relative overflow-hidden bg-cmic-900 text-white rounded-2xl p-8 lg:p-10 shadow-xl border border-cmic-700 w-full"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30 mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: `url(${TEXTURE_URL})` }}
        />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-md px-3 py-1 rounded-full text-sm font-medium border border-white/20 mb-5 text-cmic-50">
            <ShieldCheck size={15} /> {membership.category_label || T('Membresía activa', 'Active membership')}
          </span>
          <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-1 text-white">
            {T('Membresía al corriente', 'Membership up to date')}
          </h2>
          {membership.paid_until && (
            <p className="text-cmic-200 text-base lg:text-lg mb-6 flex items-center gap-2">
              <Calendar size={16} /> {T('Vigente hasta', 'Valid until')} {fmtDate(membership.paid_until, isEs)}
              {typeof days === 'number' && (
                <span className="text-cmic-300 text-sm">· {days} {T('días restantes', 'days left')}</span>
              )}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-5 border-t border-white/10">
            {benefits.map((b) => (
              <div key={b} className="flex items-center gap-2.5 text-sm text-cmic-100 font-medium">
                <CheckCircle2 size={16} className="text-emerald-300 flex-shrink-0" /> {b}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── POR VENCER (current but ≤45 days) ─────────────────────────────────────
  if (expiringSoon) {
    return (
      <div
        data-testid="membresia-card-por-vencer"
        className="relative bg-white rounded-2xl p-8 lg:p-10 shadow-lg border-2 border-amber-400 overflow-hidden w-full"
      >
        <div className="flex items-center gap-2 text-amber-600 font-bold mb-4 bg-amber-50 w-fit px-3 py-1 rounded-full text-sm">
          <AlertTriangle size={15} /> {T('Tu membresía está por vencer', 'Your membership is expiring soon')}
        </div>
        <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-charcoal-900 mb-1">
          {days <= 0 ? T('Vence hoy', 'Expires today') : `${T('Vence en', 'Expires in')} ${days} ${T('días', 'days')}`}
        </h2>
        <p className="text-charcoal-600 text-base lg:text-lg mb-6 flex items-center gap-2">
          <Calendar size={16} className="text-amber-500" /> {T('Vigencia hasta', 'Valid until')} {fmtDate(membership.paid_until, isEs)}
        </p>
        {owed > 0 && (
          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-3xl lg:text-4xl font-bold text-charcoal-900">{money(owed)}</span>
            <span className="text-sm text-charcoal-500 font-medium">{T('por pagar', 'owed')}</span>
          </div>
        )}
        <Link
          to={profileHref}
          data-testid="renovar-membresia-btn"
          className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white shadow-md rounded-lg px-7 py-3 font-bold transition-all transform hover:-translate-y-0.5"
        >
          {T('Renovar membresía', 'Renew membership')} <ArrowRight size={17} />
        </Link>
      </div>
    );
  }

  // ── VENCIDA (expired) ─────────────────────────────────────────────────────
  if (membership.status === 'expired') {
    return (
      <div
        data-testid="membresia-card-vencida"
        className="relative bg-red-50 rounded-2xl p-8 lg:p-10 border-2 border-red-200 shadow-sm w-full"
      >
        <div className="flex items-center gap-2 text-red-700 font-bold mb-4 bg-red-100/60 w-fit px-3 py-1 rounded-full text-sm">
          <AlertTriangle size={15} /> {T('Membresía vencida', 'Membership expired')}
        </div>
        <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-red-900 mb-1">
          {T('Tu membresía ha vencido', 'Your membership has expired')}
        </h2>
        <p className="text-red-700/80 text-base lg:text-lg font-medium mb-6 flex items-center gap-2">
          <Calendar size={16} /> {T('Venció el', 'Expired on')} {fmtDate(membership.paid_until, isEs)}
          {typeof days === 'number' && days < 0 && (
            <span className="text-sm">· {Math.abs(days)} {T('días atrás', 'days ago')}</span>
          )}
        </p>
        {owed > 0 && (
          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-3xl lg:text-4xl font-bold text-red-900">{money(owed)}</span>
            <span className="text-sm text-red-700/80 font-medium">{T('por pagar', 'owed')}</span>
          </div>
        )}
        <Link
          to={profileHref}
          data-testid="renovar-membresia-btn"
          className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white shadow-md rounded-lg px-7 py-3 font-bold transition-all transform hover:-translate-y-0.5"
        >
          {T('Regularizar mi membresía', 'Reactivate my membership')} <ArrowRight size={17} />
        </Link>
      </div>
    );
  }

  // ── SUSPENDIDA ────────────────────────────────────────────────────────────
  if (membership.status === 'suspended') {
    return (
      <div
        data-testid="membresia-card-suspendida"
        className="relative bg-charcoal-50 rounded-2xl p-8 border border-charcoal-200 shadow-sm w-full"
      >
        <div className="flex items-center gap-2 text-charcoal-600 font-bold mb-3 text-sm">
          <Ban size={15} /> {T('Cuenta suspendida', 'Account suspended')}
        </div>
        <h2 className="text-xl lg:text-2xl font-bold tracking-tight text-charcoal-800 mb-1">
          {T('Tu perfil está temporalmente oculto', 'Your profile is temporarily hidden')}
        </h2>
        <p className="text-charcoal-500 text-sm">
          {T('Contacta a la mesa directiva para más información.', 'Contact the board for more information.')}
        </p>
      </div>
    );
  }

  // ── SIN FECHA (unset) ─────────────────────────────────────────────────────
  return (
    <div
      data-testid="membresia-card-sin-fecha"
      className="relative bg-white rounded-2xl p-8 border border-charcoal-200 shadow-sm w-full"
    >
      <div className="flex items-center gap-2 text-charcoal-500 font-semibold mb-3 text-sm">
        <Clock size={15} /> {T('Sin fecha de vigencia registrada', 'No validity date on file')}
      </div>
      <h2 className="text-xl lg:text-2xl font-bold tracking-tight text-charcoal-800 mb-1">
        {membership.category_label || T('Miembro del Colegio', 'College member')}
      </h2>
      <p className="text-charcoal-500 text-sm mb-5">
        {T('Aún no se ha registrado tu fecha de pago de cuotas.', 'Your dues payment date has not been recorded yet.')}
      </p>
      <Link
        to={profileHref}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-cmic-600 hover:text-cmic-700"
      >
        {T('Ver mi perfil', 'View my profile')} <ArrowRight size={15} />
      </Link>
    </div>
  );
}
